import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { ArrowUpRight } from 'lucide-react'
import { benchmarkUrl } from '../lib/constants/benchmark'

export function Footer() {
  const { t, i18n } = useTranslation('home')
  const currentYear = new Date().getFullYear()
  const [currentLang, setCurrentLang] = useState(i18n.language || 'en')

  useEffect(() => {
    // Get language from i18n or localStorage
    const lang = i18n.language || localStorage.getItem('i18nextLng') || 'en'
    setCurrentLang(lang)
  }, [i18n.language])

  const footerLinks = {
    company: [
      { name: t('footer.about'), href: '#about', external: false },
      { name: t('footer.team'), href: '#team', external: false },
      { name: t('footer.benchmark'), href: benchmarkUrl(currentLang), external: true },
    ],
    legal: [
      { name: t('footer.imprint'), href: `/${currentLang}/imprint` },
      { name: t('footer.privacyPolicy'), href: `/${currentLang}/privacy` },
      { name: t('footer.termsOfService'), href: `/${currentLang}/terms` },
    ],
    contact: [
      { name: 'info@securesight.ai', href: 'mailto:info@securesight.ai' },
      { name: 'support@securesight.ai', href: 'mailto:support@securesight.ai' },
    ],
  }

  return (
    <footer className="footer-section-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <motion.h3
              className="footer-logo text-2xl font-bold mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              securesight.ai
            </motion.h3>
            <motion.p
              className="footer-tagline mb-4 max-w-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {t('footer.tagline')}
            </motion.p>
          </div>

          {/* Company Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h4 className="footer-heading font-semibold mb-4">{t('footer.company')}</h4>
            <ul className="space-y-2">
              {footerLinks.company.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className={link.external ? 'footer-link inline-flex items-center gap-1' : 'footer-link'}
                    {...(link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  >
                    {link.name}
                    {link.external && <ArrowUpRight size={13} aria-hidden="true" className="opacity-70" />}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Legal Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h4 className="footer-heading font-semibold mb-4">{t('footer.legal')}</h4>
            <ul className="space-y-2">
              {footerLinks.legal.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="footer-link"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <h4 className="footer-heading font-semibold mb-4">{t('footer.contact')}</h4>
            <ul className="space-y-2">
              {footerLinks.contact.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.href}
                    className="footer-link"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Bottom Bar */}
        <motion.div
          className="footer-bottom-bar mt-12 pt-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="footer-copyright text-sm">
              © {currentYear} <span className="footer-logo-small">securesight.ai</span>. {t('footer.allRightsReserved')}
            </p>
            <p className="footer-copyright text-sm mr-16 md:mr-0">
              {t('footer.madeWithLove')}
            </p>
          </div>
        </motion.div>
      </div>
    </footer>
  )
}
